import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { useOptionalChatDock } from "@components/chat-dock";
import { useSwitchActiveCompany } from "@components/shared/hooks/useSwitchActiveCompany";
import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { useModals } from "../../context/ModalContext";
import { useSocket } from "../../context/SocketContext";
import { ENotificationType } from "../interfaces";
import { useWatchBaseNotification } from "../hooks/useWatchBaseNotification";
import { showBrowserNotification } from "./showBrowserNotification";
import {
  ENotificationTargetKind,
  getNotificationChatMeta,
  getNotificationChatTarget,
  getNotificationCompanyId,
  getNotificationTarget,
  getNotificationTriggerId,
  triggerNotificationTarget,
} from "../../components/notifications/notificationHelpers";
import { NotificationModals } from "../../components/notifications/NotificationModals";
import { getModalKeysByStackOrder, saveNotificationReturnState } from "../../components/notifications/notificationReturnState";
import { getNotificationCompanyName } from "../../components/notifications/useEnsureNotificationCompany";

const browserNotificationTypes = [
  ENotificationType.JobScheduled,
  ENotificationType.JobPunched,
  ENotificationType.JobRescheduled,
  ENotificationType.JobCancelled,
  ENotificationType.JobInProgress,
  ENotificationType.JobCompleted,
  ENotificationType.JobAssigned,
  ENotificationType.JobAccepted,
  ENotificationType.JobDeclined,
  ENotificationType.JobReminder,
  ENotificationType.InvoicePaid,
  ENotificationType.InvoicePaidDeposit,
  ENotificationType.QuoteAccepted,
  ENotificationType.AdminInvitationReceived,
  ENotificationType.InboundMessageReceived,
  ENotificationType.InternalMessageReceived,
  ENotificationType.InternalGroupMessageReceived,
  ENotificationType.InternalGroupMentionReceived,
];

const parseNotification = (raw: unknown) => {
  if (!raw) return null;

  if (typeof raw === "string") {
    try {
      return parseNotification(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  return typeof raw === "object" ? raw : null;
};

export function BrowserNotificationBridge() {
  const location = useLocation();
  const { socket } = useSocket();
  const recentlyShownRef = useRef<Map<string, number>>(new Map());
  const [notificationTargets, setNotificationTargets] = useState([]);
  const companies = useSelector((state: any) => state.userState?.companies);
  const activeCompanyId = useSelector((state: any) => state.userState?.activeCompanyId ?? state.userState?.company?.id);
  const activeCompanyIdRef = useRef(activeCompanyId);
  const chatDock = useOptionalChatDock();
  const { openCalls } = useFloatingBubbles();
  const { modals, showDialog } = useModals();
  const { switchActiveCompany } = useSwitchActiveCompany();

  activeCompanyIdRef.current = activeCompanyId;

  const rememberNotificationTarget = useCallback((notification) => {
    const target = getNotificationTarget(notification);
    if (!target || target.kind === ENotificationTargetKind.AdminInvitation) return;

    const triggerId = getNotificationTriggerId(target);
    if (!triggerId) return;

    setNotificationTargets((currentTargets) => {
      if (currentTargets.some((currentTarget) => getNotificationTriggerId(currentTarget) === triggerId)) {
        return currentTargets;
      }

      return [...currentTargets, target];
    });
  }, []);

  const openBrowserNotification = useCallback(
    async (notification) => {
      const target = getNotificationTarget(notification);
      const chatTarget = getNotificationChatTarget(notification);
      if (!target && !chatTarget) return;

      const continueOpenNotification = async () => {
        try {
          const notificationCompanyId = getNotificationCompanyId(notification);
          if (notificationCompanyId && String(notificationCompanyId) !== String(activeCompanyIdRef.current)) {
            saveNotificationReturnState({
              companyId: activeCompanyIdRef.current,
              pathname: location.pathname,
              search: location.search,
              hash: location.hash,
              modalKeys: getModalKeysByStackOrder(modals),
            });
            await switchActiveCompany(notificationCompanyId);
          }

          if (target && target.kind !== ENotificationTargetKind.AdminInvitation && triggerNotificationTarget(target)) {
            return;
          }

          if (chatTarget && chatDock) {
            openCalls();
            chatDock.openChat(chatTarget, getNotificationChatMeta(notification, companies ?? []));
          }
        } catch (error) {
          console.error("Error opening browser notification:", error);
        }
      };

      try {
        const notificationCompanyId = getNotificationCompanyId(notification);
        if (notificationCompanyId && String(notificationCompanyId) !== String(activeCompanyIdRef.current)) {
          const companyName = getNotificationCompanyName(notification, companies ?? []) || "another company";

          showDialog({
            type: "confirm",
            title: "Switch company?",
            message: `You are about to switch to ${companyName} to open this notification. Are you sure?`,
            confirmLabel: "Yes, switch",
            cancelLabel: "Cancel",
            onCancel: () => {},
            onConfirm: () => {
              void continueOpenNotification();
            },
          });
          return;
        }

        await continueOpenNotification();
      } catch (error) {
        console.error("Error opening browser notification:", error);
      }
    },
    [chatDock, companies, location.hash, location.pathname, location.search, modals, openCalls, showDialog, switchActiveCompany],
  );

  const showOnce = useCallback(
    (notification) => {
      const key = `${notification.type ?? "notification"}-${notification.id ?? notification.createdAt ?? notification.message ?? ""}`;
      const now = Date.now();
      const recentlyShown = recentlyShownRef.current;

      recentlyShown.forEach((shownAt, storedKey) => {
        if (now - shownAt > 10000) recentlyShown.delete(storedKey);
      });

      if (recentlyShown.has(key)) return;

      recentlyShown.set(key, now);
      rememberNotificationTarget(notification);
      showBrowserNotification(notification, {
        onClick: () => {
          void openBrowserNotification(notification);
        },
      });
    },
    [openBrowserNotification, rememberNotificationTarget],
  );

  useWatchBaseNotification({
    notificationTypes: browserNotificationTypes,
    dissableSnackbarAlert: true,
    onNotificationArrived: (notification) => {
      if (notification) showOnce(notification);
    },
  });

  useEffect(() => {
    if (!socket) return;

    const handleGenericNotification = (raw) => {
      const notification = parseNotification(raw);
      if (!notification) return;

      showOnce(notification);
    };

    socket.on("notification", handleGenericNotification);

    return () => {
      socket.off("notification", handleGenericNotification);
    };
  }, [socket, showOnce]);

  return <NotificationModals targets={notificationTargets} keyPrefix="BrowserNotification" />;
}
